import { useEffect, useRef } from 'react';
import { clamp, subscribeScroll, type ScrollState } from '@components/motion';
import { Img } from '@/components/Img';

const PROTEST_BG = '/landing/problem/protest-bg.webp';
const SECONDARY_IMAGE = '/landing/problem/secondary-image.webp';
const QUOTE_MARK = '/landing/problem/quote-mark.svg';

/** 16 evenly-spaced tick marks per group, matching the Figma ruler. */
const TICKS = Array.from({ length: 17 });

function TickGroup() {
    return (
        <div className="relative flex shrink-0 items-center gap-[114px]">
            {TICKS.map((_, i) => (
                <div key={i} className="relative h-[36px] w-[4px] shrink-0 rounded-[3px] bg-[#3f3f3f]" />
            ))}
        </div>
    );
}

const DESIGN_H = 1092;
const TRACK_W = 8268;
/** Tall scroll runway: maps vertical scroll → horizontal track travel while pinned. */
const SCENE_VH = 420;

function ProblemDesktop() {
    const sceneRef = useRef<HTMLElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const bgRef = useRef<HTMLDivElement>(null);
    const barRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const scene = sceneRef.current;
        const track = trackRef.current;
        if (!scene || !track) return;
        const apply = (state: ScrollState): void => {
            const rect = scene.getBoundingClientRect();
            const distance = rect.height - state.viewportHeight;
            const p = distance > 0 ? clamp(-rect.top / distance) : 0;
            // Fit the 1092px-tall design stage to the viewport height; scroll maps to X travel.
            const s = state.viewportHeight / DESIGN_H;
            const travel = TRACK_W - state.viewportWidth / s; // remaining design px to traverse
            track.style.transform = `scale(${s}) translateX(${-p * travel}px)`;
            // Background drifts slower than the foreground text → parallax depth.
            if (bgRef.current) bgRef.current.style.transform = `translateX(${p * 260}px)`;
            // Story timeline progress.
            if (barRef.current) barRef.current.style.width = `${p * 100}%`;
        };
        return subscribeScroll(apply);
    }, []);

    return (
        <section ref={sceneRef} className="relative bg-[#0c0c0d]" style={{ height: `${SCENE_VH}vh` }}>
            <div className="sticky top-0 h-dvh w-full overflow-hidden">
                {/* The full storytelling track — scrubbed horizontally while the scene is pinned. */}
                <div
                    ref={trackRef}
                    className="absolute left-0 top-0 h-[1092px] w-[8268px] origin-top-left text-white will-change-transform"
                >
                    {/* Background protest image with dark overlay (parallax layer) */}
                    <div
                        ref={bgRef}
                        className="absolute left-[-3px] top-1/2 h-[1089px] w-[1920px] -translate-y-1/2 will-change-transform"
                    >
                        <Img
                            src={PROTEST_BG}
                            alt=""
                            className="pointer-events-none absolute inset-0 size-full max-w-none object-cover"
                        />
                        <div className="absolute inset-0 bg-[rgba(0,0,0,0.87)]" />
                    </div>

                    {/* Gradient fade that masks the right edge of the background image */}
                    <div
                        className="absolute left-[1511px] top-1/2 h-[1025px] w-[3486px] -translate-y-1/2 backdrop-blur-[11.55px]"
                        style={{
                            backgroundImage: 'linear-gradient(90.08deg, rgba(12,12,13,0) 0%, rgb(12,12,13) 10.1%)',
                        }}
                    />

                    {/* Heading: The Problem We Are Solving */}
                    <div className="absolute left-[158px] top-1/2 flex w-[802px] -translate-y-1/2 flex-col justify-center text-[110px] font-normal">
                        <p className="leading-[144px]">
                            <span>The </span>
                            <span className="text-[#f60]">Problem </span>
                            <span>We Are Solving</span>
                        </p>
                    </div>

                    {/* Ruler / tick marks */}
                    <div className="absolute left-[44px] top-[1002px] flex items-center gap-[105px]">
                        <TickGroup />
                        <TickGroup />
                        <TickGroup />
                        <TickGroup />
                        <TickGroup />
                    </div>

                    {/* --- Panel 1: It always starts the same way --- */}
                    <Img src={QUOTE_MARK} alt="" className="absolute left-[1599px] top-[113px] h-[49px] w-[25px]" />
                    <div className="absolute left-[1665px] top-[184px] flex w-[612px] -translate-y-1/2 flex-col justify-center text-[72px] font-semibold text-white">
                        <p className="leading-[92px]">It always starts the same way.</p>
                    </div>
                    <div className="absolute left-[1813px] top-[356.5px] flex -translate-y-1/2 flex-col justify-center whitespace-nowrap text-[48px] font-light italic text-[#959595]">
                        <p className="leading-[61px]">Someone&rsquo;s rights are violated.</p>
                    </div>
                    <div className="absolute left-[1917px] top-[559px] flex h-[276px] w-[716px] -translate-y-1/2 flex-col justify-center text-[36px] font-normal text-white">
                        <p className="whitespace-pre-wrap leading-[61px]">
                            {
                                ' A salary is withheld.\n A tenant is forcefully evicted.\n A young person is arrested without cause.\n A family waits endlessly for justice that never comes.'
                            }
                        </p>
                    </div>
                    <div className="absolute left-[1917px] top-[785px] flex -translate-y-1/2 flex-col justify-center whitespace-nowrap text-[32px] font-light italic text-[#959595]">
                        <p className="mb-0 leading-[54px]">At that moment, the law should protect them.</p>
                        <p className="leading-[54px]">
                            But for millions of people, the law feels distant, expensive, and unreachable.
                        </p>
                    </div>

                    {/* --- Panel 2 --- */}
                    <div className="absolute left-[3254px] top-[266.5px] flex h-[337px] w-[851px] -translate-y-1/2 flex-col justify-center text-[56px] font-bold text-white">
                        <p className="leading-[61px]">
                            This days, knowing you are wronged is one thing.
                            <br />
                            Knowing where to go, who to speak to, or how to begin is another.
                        </p>
                    </div>
                    <div className="absolute left-[3524px] top-[558.5px] flex h-[337px] w-[851px] -translate-y-1/2 flex-col justify-center whitespace-pre-wrap text-[36px] font-normal text-[#a6a6a6]">
                        <p className="mb-0 leading-[61px]">
                            Legal processes are complex.
                            <br />
                            {' Legal language is intimidating.'}
                            <br />
                            {' Legal help is often priced beyond reach.'}
                        </p>
                        <p className="leading-[61px]">
                            So people endure injustice silently — not because they accept it,
                            <br />
                            {' but because they don’t know how to fight it.'}
                        </p>
                    </div>

                    {/* --- Panel 3: And when people cannot access justice (text sits in front of the image) --- */}
                    <div className="absolute left-[4728px] top-[calc(50%-176px)] z-10 flex w-[802px] -translate-y-1/2 flex-col justify-center text-[90px] font-normal text-white">
                        <p>
                            <span className="leading-[118px]">And when people cannot access justice, something </span>
                            <span className="leading-[118px] text-[#f60]">worse happens.</span>
                        </p>
                    </div>
                    {/* Sub-copy sits directly under "worse happens.", left-aligned */}
                    <div className="absolute left-[4728px] top-[684px] z-10 flex w-[447px] flex-col text-[36px] font-normal text-[#a6a6a6]">
                        <p className="mb-0 leading-[61px]">Abuse goes unchecked.</p>
                        <p className="leading-[61px]">Power is exercised without accountability.</p>
                    </div>

                    {/* Secondary image (mirrored, as in Figma) */}
                    <div className="absolute left-[5256px] top-1/2 flex h-[824px] w-[499px] -translate-y-1/2 items-center justify-center">
                        <div className="flex-none rotate-180 -scale-y-100">
                            <div className="relative h-[824px] w-[499px]">
                                <div className="absolute inset-0 overflow-hidden">
                                    <Img
                                        src={SECONDARY_IMAGE}
                                        alt=""
                                        className="pointer-events-none absolute left-[-51.44%] top-0 h-full w-[247.7%] max-w-none object-cover"
                                    />
                                </div>
                                <div className="absolute inset-0 bg-[rgba(0,0,0,0.59)]" />
                            </div>
                        </div>
                    </div>

                    {/* --- Panel 4 --- */}
                    <div className="absolute left-[5792px] top-[182px] flex h-[152px] w-[447px] -translate-y-1/2 flex-col justify-center text-[36px] font-normal text-[#a6a6a6]">
                        <p className="leading-[61px]">
                            Citizens lose trust — not just in the system, but in the idea of fairness itself.
                        </p>
                    </div>
                    <div className="absolute left-[5792px] top-[813px] flex h-[210px] w-[594px] -translate-y-1/2 flex-col justify-center text-[36px] font-normal text-white">
                        <p className="whitespace-pre-wrap leading-[61px]">
                            When wrongdoing is not challenged, it becomes normal.
                            <br />
                            {' When injustice is not recorded, it disappears.'}
                        </p>
                    </div>

                    {/* --- Panel 5: The system was not built for visibility (sits in front of the panel) --- */}
                    <Img
                        src={QUOTE_MARK}
                        alt=""
                        className="absolute left-[6501px] top-[187px] z-10 h-[49px] w-[25px]"
                    />
                    <div className="absolute left-[6567px] top-[262px] z-10 flex w-[693px] -translate-y-1/2 flex-col justify-center text-[72px] font-semibold text-white">
                        <p className="leading-[92px]">The system was not built for visibility.</p>
                    </div>

                    {/* Dark inequality panel — `isolate` keeps its inner z-stack self-contained so the
                        heading above can sit in front of it */}
                    <div className="absolute left-[6739px] top-[101px] h-[827px] w-[1529px] overflow-clip bg-[#151515] isolate">
                        <div className="absolute left-[540px] top-[147.5px] flex h-[95px] w-[545px] -translate-y-1/2 flex-col justify-center text-[36px] font-normal text-[#a6a6a6]">
                            <p className="leading-[61px]">
                                Cases are buried in paperwork.
                                <br />
                                Complaints are lost in silence.
                            </p>
                        </div>
                        <div className="absolute left-[540px] top-[281.5px] flex h-[95px] w-[845px] -translate-y-1/2 flex-col justify-center text-[36px] font-normal text-white">
                            <p className="leading-[61px]">
                                Without transparency, justice cannot learn.
                                <br />
                                Without data, reform cannot happen.
                            </p>
                        </div>
                        {/* Lighter sub-panel — sits behind the copy below */}
                        <div className="absolute bottom-0 right-0 z-0 h-[351px] w-[1163px] bg-[#343434]" />
                        <div className="absolute left-[485px] top-[595px] z-10 flex w-[485px] -translate-x-full -translate-y-1/2 flex-col justify-center text-right text-[36px] font-extrabold text-[#f60]">
                            <p className="leading-[61px]">This is how inequality becomes permanent.</p>
                        </div>
                        <div className="absolute left-[540px] top-[650px] z-10 flex h-[206px] w-[845px] -translate-y-1/2 flex-col justify-center whitespace-pre-wrap text-[36px] font-normal text-white">
                            <p className="mb-0 leading-[61px]">
                                Those with money navigate the system.
                                <br />
                                {' Those without it are trapped outside.'}
                            </p>
                            <p className="leading-[61px]">
                                Justice becomes something you qualify for,
                                <br />
                                {' not something you are entitled to.'}
                            </p>
                        </div>
                    </div>
                </div>
                {/* Story timeline / scroll progress line */}
                <div className="pointer-events-none absolute bottom-7 left-1/2 h-[3px] w-[240px] -translate-x-1/2 overflow-hidden rounded-full bg-white/15">
                    <div ref={barRef} className="h-full w-0 rounded-full bg-[#f60]" />
                </div>
            </div>
        </section>
    );
}

/** One full-width slide in the mobile horizontal story track. */
function MobilePanel({ children }: { children: React.ReactNode }) {
    return <div className="flex h-full w-screen shrink-0 flex-col justify-center px-5 pb-20 pt-24">{children}</div>;
}

/** Number of slides in the mobile track (drives the scroll runway length). */
const MOBILE_PANELS = 6;

function ProblemMobile() {
    const sceneRef = useRef<HTMLElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const bgRef = useRef<HTMLDivElement>(null);
    const barRef = useRef<HTMLDivElement>(null);

    // Same mechanic as desktop: vertical scroll through a tall runway scrubs the
    // pinned track horizontally, with the background drifting for depth.
    useEffect(() => {
        const scene = sceneRef.current;
        const track = trackRef.current;
        if (!scene || !track) return;
        const apply = (state: ScrollState): void => {
            const rect = scene.getBoundingClientRect();
            const distance = rect.height - state.viewportHeight;
            const p = distance > 0 ? clamp(-rect.top / distance) : 0;
            const travel = Math.max(0, track.scrollWidth - state.viewportWidth);
            track.style.transform = `translate3d(${-p * travel}px, 0, 0)`;
            if (bgRef.current) bgRef.current.style.transform = `translate3d(${p * 60}px, 0, 0)`;
            if (barRef.current) barRef.current.style.width = `${p * 100}%`;
        };
        return subscribeScroll(apply);
    }, []);

    return (
        <section ref={sceneRef} className="relative bg-[#0c0c0d]" style={{ height: `${MOBILE_PANELS * 100}vh` }}>
            <div className="sticky top-0 h-dvh w-full overflow-hidden text-white">
                {/* Background protest image with dark overlay (parallax layer) */}
                <div ref={bgRef} className="absolute inset-0 will-change-transform">
                    <Img src={PROTEST_BG} alt="" className="absolute inset-0 size-full object-cover" />
                    <div className="absolute inset-0 bg-[rgba(0,0,0,0.87)]" />
                </div>

                {/* Horizontal story track */}
                <div ref={trackRef} className="relative flex h-full will-change-transform">
                    {/* Slide 1 — heading */}
                    <MobilePanel>
                        <h2 className="text-[44px] font-normal leading-[54px]">
                            The <span className="text-[#f60]">Problem</span> We Are Solving
                        </h2>
                    </MobilePanel>

                    {/* Slide 2 */}
                    <MobilePanel>
                        <Img src={QUOTE_MARK} alt="" className="h-[34px] w-[17px]" />
                        <h3 className="mt-4 text-[30px] font-semibold leading-[38px]">
                            It always starts the same way.
                        </h3>
                        <p className="mt-5 text-[18px] font-light italic leading-[26px] text-[#959595]">
                            Someone&rsquo;s rights are violated.
                        </p>
                        <p className="mt-5 whitespace-pre-line text-[15px] leading-[24px] text-white">
                            {
                                'A salary is withheld.\nA tenant is forcefully evicted.\nA young person is arrested without cause.\nA family waits endlessly for justice that never comes.'
                            }
                        </p>
                        <p className="mt-5 text-[14px] font-light italic leading-[22px] text-[#959595]">
                            At that moment, the law should protect them. But for millions of people, the law feels
                            distant, expensive, and unreachable.
                        </p>
                    </MobilePanel>

                    {/* Slide 3 */}
                    <MobilePanel>
                        <h3 className="text-[24px] font-bold leading-[34px]">
                            This days, knowing you are wronged is one thing. Knowing where to go, who to speak to, or
                            how to begin is another.
                        </h3>
                        <p className="mt-6 whitespace-pre-line text-[16px] leading-[26px] text-[#a6a6a6]">
                            {
                                'Legal processes are complex.\nLegal language is intimidating.\nLegal help is often priced beyond reach.'
                            }
                        </p>
                        <p className="mt-6 text-[16px] leading-[26px] text-[#a6a6a6]">
                            So people endure injustice silently — not because they accept it, but because they
                            don&rsquo;t know how to fight it.
                        </p>
                    </MobilePanel>

                    {/* Slide 4 */}
                    <MobilePanel>
                        <h3 className="text-[32px] font-normal leading-[42px]">
                            And when people cannot access justice, something{' '}
                            <span className="text-[#f60]">worse happens.</span>
                        </h3>
                        <p className="mt-6 whitespace-pre-line text-[18px] leading-[30px] text-[#a6a6a6]">
                            {'Abuse goes unchecked.\nPower is exercised without accountability.'}
                        </p>
                        <div className="relative mt-8 h-[200px] w-full overflow-hidden rounded-[12px]">
                            <Img src={SECONDARY_IMAGE} alt="" className="absolute inset-0 size-full object-cover" />
                            <div className="absolute inset-0 bg-[rgba(0,0,0,0.59)]" />
                        </div>
                    </MobilePanel>

                    {/* Slide 5 */}
                    <MobilePanel>
                        <p className="text-[20px] leading-[32px] text-[#a6a6a6]">
                            Citizens lose trust — not just in the system, but in the idea of fairness itself.
                        </p>
                        <p className="mt-6 whitespace-pre-line text-[20px] leading-[32px] text-white">
                            {
                                'When wrongdoing is not challenged, it becomes normal.\nWhen injustice is not recorded, it disappears.'
                            }
                        </p>
                        <Img src={QUOTE_MARK} alt="" className="mt-10 h-[34px] w-[17px]" />
                        <h3 className="mt-4 text-[28px] font-semibold leading-[36px]">
                            The system was not built for visibility.
                        </h3>
                    </MobilePanel>

                    {/* Slide 6 — inequality panel + CTA */}
                    <MobilePanel>
                        <div className="-mx-5 bg-[#151515] px-5 py-8">
                            <p className="whitespace-pre-line text-[16px] leading-[26px] text-[#a6a6a6]">
                                {'Cases are buried in paperwork.\nComplaints are lost in silence.'}
                            </p>
                            <p className="mt-5 whitespace-pre-line text-[16px] leading-[26px] text-white">
                                {'Without transparency, justice cannot learn.\nWithout data, reform cannot happen.'}
                            </p>
                            <div className="-mx-5 mt-7 bg-[#343434] px-5 py-7">
                                <p className="text-[20px] font-extrabold leading-[30px] text-[#f60]">
                                    This is how inequality becomes permanent.
                                </p>
                                <p className="mt-4 whitespace-pre-line text-[16px] leading-[26px] text-white">
                                    {'Those with money navigate the system.\nThose without it are trapped outside.'}
                                </p>
                                <p className="mt-4 text-[16px] leading-[26px] text-white">
                                    Justice becomes something you qualify for, not something you are entitled to.
                                </p>
                            </div>
                        </div>
                    </MobilePanel>
                </div>

                {/* Story timeline / scroll progress line */}
                <div className="pointer-events-none absolute bottom-6 left-1/2 h-[3px] w-[160px] -translate-x-1/2 overflow-hidden rounded-full bg-white/15">
                    <div ref={barRef} className="h-full w-0 rounded-full bg-[#f60]" />
                </div>
            </div>
        </section>
    );
}

export default function ProblemSection() {
    return (
        <>
            <div className="hidden lg:block">
                <ProblemDesktop />
            </div>
            <div className="lg:hidden">
                <ProblemMobile />
            </div>
        </>
    );
}
